import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { filter, map, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import {
	ArrangeOptions,
	PromiseToPay,
	PromiseToPayPermissions,
	PromiseToPayService,
	UpcomingSummary,
} from '@lmc-app/core/services/promise-to-pay.service';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import {
	ARRANGEMENT_TYPE_LABELS,
	PROMISE_TO_PAY_CANCELLABLE_STATUSES,
	PROMISE_TO_PAY_DELETABLE_STATUSES,
	PROMISE_TO_PAY_FINAL_STATUSES,
	PROMISE_TO_PAY_STATUSES,
	PAYMENT_METHOD_LABELS,
	PromiseToPayPaymentMethod,
	PromiseToPayStatus,
	PromiseToPayType,
} from '@lmc-app/shared/enum/promise-to-pay-status.enum';
import { LmcDialogComponent } from '@lmc-app/shared/components/dialog/lmc-dialog.component';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';
import {
	PromiseToPayDialogComponent,
	PromiseToPayDialogResult,
} from '@lmc-app/loan-servicing/promise-to-pay/create/promise-to-pay-dialog.component';
import { PayoffContext } from '@lmc-app/loan-servicing/promise-to-pay/create/payoff-statement';
import {
	canEditPromise,
	editDialogData,
	isPayoffArrangement,
	LoanDetailsMeta,
	toPayoffContext,
} from '@lmc-app/loan-servicing/promise-to-pay/create/promise-to-pay-edit';
import { PromiseToPayDetailDialogComponent } from '@lmc-app/loan-servicing/promise-to-pay/detail/promise-to-pay-detail-dialog.component';
import { PromiseToPayArrangeDialogComponent } from '@lmc-app/loan-servicing/promise-to-pay/arrange/promise-to-pay-arrange-dialog.component';
import { pickArrangeBankAccount } from '@lmc-app/loan-servicing/promise-to-pay/bank-account/promise-to-pay-bank-account-dialog.component';

interface FilterModel {
	paymentMethod: PromiseToPayPaymentMethod;
	status: PromiseToPayStatus | null;
	type: PromiseToPayType | null;
	loanNumber: string | null;
	promiseDateFrom: Date | null;
	promiseDateTo: Date | null;
}

/**
 * The view the screen opens on for a payment method: Requested, with no date range. The
 * date fields are part of the model but not of the form — the day cards set them.
 */
function defaultFilter(paymentMethod: PromiseToPayPaymentMethod): FilterModel {
	return {
		paymentMethod,
		status: PromiseToPayStatus.Requested,
		type: null,
		loanNumber: null,
		promiseDateFrom: null,
		promiseDateTo: null,
	};
}

/**
 * The loan-servicing promise-to-pay screen: every PTP and arrangement across every loan,
 * with the actions that move one along its lifecycle.
 *
 * Replaces the ag-grid `promise-to-pays` screen. Two behaviours are deliberately different:
 *
 *  - **No hidden year filter.** The old grid queried `YEAR(PtP_Date) = YEAR(GETDATE())` and
 *    said nothing about it, so anything from a prior year was simply invisible. Here the
 *    date range is a filter the operator sets.
 *  - **No free-form status dropdown.** The old edit dialog let any status be written by
 *    hand, NSF included. Status now moves through the actions below, or through Edit for
 *    those granted Edit; the API validates each.
 *
 * Opens on manual debit `Requested` promises due today or later. The Payment Method filter
 * switches to customer deposits, which open on `Requested` with no date range and have no
 * day-card summary. Nothing older than a year is listed.
 *
 * Customer deposits never go through KapCharge: there is no Arrange, and their outcome is
 * recorded by hand as Processed or Broken.
 *
 * Arrange raises the KapCharge payment as part of the same action, so there is no Send
 * step and no payment column: a promise that is Arranged already has its transaction
 * queued, in the PTP batch for its promise date.
 *
 * Soft-deleted promises are not in the default view — 12.5% of the manual_debit rows are
 * Deleted and none of them is work anybody has to do. Picking Deleted in the status filter
 * still finds them, which is what the legacy getDeletedPTPs report was for.
 */
@Component({
	selector: 'app-promise-to-pay-list',
	standalone: true,
	imports: [
		CommonModule,
		RouterModule,
		ReactiveFormsModule,
		FormlyModule,
		FormlyMaterialModule,
		MatButtonModule,
		MatCardModule,
		MatIconModule,
		MatMenuModule,
		MatPaginatorModule,
		MatProgressSpinnerModule,
		MatSnackBarModule,
		MatTableModule,
	],
	templateUrl: './promise-to-pay-list.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./promise-to-pay-list.component.scss'],
})
export class PromiseToPayListComponent implements OnInit, OnDestroy {
	/** Clear (x) button rendered as a matSuffix on every filter field (see template). */
	@ViewChild('clearSuffix', { static: true })
	clearSuffix!: TemplateRef<unknown>;

	readonly FormatUtil = FormatUtil;
	readonly PromiseToPayStatus = PromiseToPayStatus;

	/**
	 * Which day the list is filtered to — an ISO date, `'later'` for everything past the
	 * window, or null for no schedule filter.
	 */
	activeUpcoming: string | null = null;

	/** Day-of-week and date, so a card reads as a day rather than a number string. */
	dayLabel(date: string): string {
		return dayjs(date).format('ddd');
	}

	dateLabel(date: string): string {
		return dayjs(date).format('D MMM');
	}

	readonly displayedColumns = [
		'loanNumber',
		'customerName',
		'type',
		'amount',
		'promiseDate',
		'status',
		'owner',
		'actions',
	];

	readonly filterForm = new FormGroup({});
	filterModel: FilterModel = defaultFilter(
		PromiseToPayPaymentMethod.ManualDebit
	);
	filterFields: FormlyFieldConfig[] = [];

	rows: PromiseToPay[] = [];
	/** Totals for the next few days; null until the first load returns. */
	upcoming: UpcomingSummary | null = null;
	totalRows = 0;
	pageSize = 25;
	currentPage = 0;
	isLoading = false;

	/** Ids with an action in flight, so only the affected row's controls are disabled. */
	private readonly busyIds = new Set<number>();
	/** Set in Admin Settings → Promise to Pay. Nothing extra is granted until it loads. */
	private permissions: PromiseToPayPermissions | null = null;

	private readonly destroy$ = new Subject<void>();

	constructor(
		private readonly promiseToPayService: PromiseToPayService,
		private readonly legacyApi: LegacyApiService,
		private readonly dialog: MatDialog,
		private readonly snackBar: MatSnackBar
	) {}

	ngOnInit(): void {
		this.filterFields = this.buildFilterFields();
		this.load();
		this.promiseToPayService
			.getPermissions()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (permissions) => (this.permissions = permissions),
				// Without them the list works as for an employee with none.
				error: () => undefined,
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildFilterFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroupClassName: 'filter-grid',
				fieldGroup: [
					{
						key: 'paymentMethod',
						type: 'select',
						props: {
							label: 'Payment Method',
							appearance: 'outline',
							required: true,
							options: Object.values(PromiseToPayPaymentMethod).map(
								(value) => ({
									value,
									label: PAYMENT_METHOD_LABELS[value],
								})
							),
							change: () => this.onPaymentMethodChange(),
						},
					},
					{
						key: 'loanNumber',
						type: 'input',
						props: {
							label: 'Loan Number',
							placeholder: 'Any loan',
							appearance: 'outline',
							suffix: this.clearSuffix,
						},
					},
					{
						key: 'status',
						type: 'select',
						props: {
							label: 'Status',
							placeholder: 'All statuses',
							appearance: 'outline',
							suffix: this.clearSuffix,
							// Deleted is offered but is not part of the default view — the
							// server excludes it unless it is asked for by name.
							options: PROMISE_TO_PAY_STATUSES.map((value) => ({
								value,
								label:
									value === PromiseToPayStatus.Deleted
										? 'Deleted (hidden by default)'
										: value,
							})),
						},
					},
					{
						key: 'type',
						type: 'select',
						props: {
							label: 'Type',
							placeholder: 'All types',
							appearance: 'outline',
							suffix: this.clearSuffix,
							options: [
								{ value: PromiseToPayType.PTP, label: 'Promise to Pay' },
								{
									value: PromiseToPayType.Arrangement,
									label: 'Arrangement',
								},
							],
						},
					},
				],
			},
		];
	}

	load(): void {
		this.isLoading = true;
		this.promiseToPayService
			.list({
				status: this.filterModel.status,
				type: this.filterModel.type,
				paymentMethod: this.filterModel.paymentMethod,
				loanNumber: this.filterModel.loanNumber,
				promiseDateFrom: this.toIsoDate(this.filterModel.promiseDateFrom),
				promiseDateTo: this.toIsoDate(this.filterModel.promiseDateTo),
				page: this.currentPage,
				pageSize: this.pageSize,
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (response) => {
					this.rows = response.rows;
					this.totalRows = response.total;
					this.upcoming = response.upcoming;
					this.isLoading = false;
				},
				error: (err) => {
					this.isLoading = false;
					this.showError(err, 'Failed to load promises to pay.');
				},
			});
	}

	get isManualDebit(): boolean {
		return (
			this.filterModel.paymentMethod === PromiseToPayPaymentMethod.ManualDebit
		);
	}

	/** Each payment method opens on its own default view; the loan number is kept. */
	onPaymentMethodChange(): void {
		this.activeUpcoming = null;
		this.filterModel = {
			...defaultFilter(this.filterModel.paymentMethod),
			loanNumber: this.filterModel.loanNumber,
		};
		this.currentPage = 0;
		this.load();
	}

	private toIsoDate(value: Date | null): string | null {
		return value ? dayjs(value).format('YYYY-MM-DD') : null;
	}

	onSearch(): void {
		// A filter set by hand supersedes whichever bucket was highlighted.
		this.activeUpcoming = null;
		this.currentPage = 0;
		this.load();
	}

	/**
	 * Filters the list to one day of the schedule — Requested promises falling on it — or
	 * clears the filter when the active card is clicked again.
	 *
	 * Writes into the same filter model the form is bound to rather than filtering
	 * client-side, so the date range is visible in the fields and the operator can widen or
	 * narrow it from there instead of wondering what the list is showing.
	 */
	selectUpcomingDay(date: string): void {
		this.applyUpcoming(date, dayjs(date).toDate(), dayjs(date).toDate());
	}

	/** Everything due past the window — an open-ended range, so no upper bound. */
	selectUpcomingLater(): void {
		this.applyUpcoming(
			'later',
			dayjs(this.upcoming!.later.from).toDate(),
			null
		);
	}

	private applyUpcoming(key: string, from: Date, to: Date | null): void {
		const clearing = this.activeUpcoming === key;
		this.activeUpcoming = clearing ? null : key;

		this.filterModel = clearing
			? defaultFilter(this.filterModel.paymentMethod)
			: {
					...this.filterModel,
					status: PromiseToPayStatus.Requested,
					promiseDateFrom: from,
					promiseDateTo: to,
			  };

		this.currentPage = 0;
		this.load();
	}

	onReset(): void {
		this.activeUpcoming = null;
		this.filterModel = defaultFilter(this.filterModel.paymentMethod);
		this.currentPage = 0;
		this.load();
	}

	onPageChange(event: PageEvent): void {
		this.currentPage = event.pageIndex;
		this.pageSize = event.pageSize;
		this.load();
	}

	trackById(_index: number, row: PromiseToPay): number {
		return row.id;
	}

	isBusy(row: PromiseToPay): boolean {
		return this.busyIds.has(row.id);
	}

	typeLabel(row: PromiseToPay): string {
		if (row.type !== PromiseToPayType.Arrangement) return 'Promise to Pay';
		return row.arrangementType
			? ARRANGEMENT_TYPE_LABELS[row.arrangementType]
			: 'Arrangement';
	}

	canEdit(row: PromiseToPay): boolean {
		return canEditPromise(this.permissions);
	}

	canArrange(row: PromiseToPay): boolean {
		return (
			row.paymentMethod === PromiseToPayPaymentMethod.ManualDebit &&
			row.status === PromiseToPayStatus.Requested
		);
	}

	/** Processed / Broken: a customer deposit's outcome, recorded by hand. */
	canCloseDeposit(row: PromiseToPay): boolean {
		return (
			row.paymentMethod === PromiseToPayPaymentMethod.CustomerDeposit &&
			!PROMISE_TO_PAY_FINAL_STATUSES.includes(row.status)
		);
	}

	canCancel(row: PromiseToPay): boolean {
		return PROMISE_TO_PAY_CANCELLABLE_STATUSES.includes(row.status);
	}

	canDelete(row: PromiseToPay): boolean {
		return PROMISE_TO_PAY_DELETABLE_STATUSES.includes(row.status);
	}

	/** The promise's facts and its full history. Read-only. */
	onViewHistory(row: PromiseToPay): void {
		this.dialog.open(PromiseToPayDetailDialogComponent, {
			width: '640px',
			autoFocus: false,
			data: { promise: row },
		});
	}

	/** Pay Off arrangements first read the loan's details for the payoff quote, as the loan page has them. */
	onEdit(row: PromiseToPay): void {
		if (!isPayoffArrangement(row)) {
			this.openEditDialog(row);
			return;
		}
		this.busyIds.add(row.id);
		this.legacyApi
			.getLoandetails(row.acctrefno)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res) => {
					this.busyIds.delete(row.id);
					const meta = (res as { meta?: LoanDetailsMeta[] })?.meta?.[0];
					this.openEditDialog(row, meta ? toPayoffContext(meta) : undefined);
				},
				error: (err) => {
					this.busyIds.delete(row.id);
					this.showError(
						err,
						'Failed to load loan details for the payoff quote.'
					);
				},
			});
	}

	private openEditDialog(row: PromiseToPay, payoff?: PayoffContext): void {
		this.dialog
			.open(PromiseToPayDialogComponent, {
				width: '640px',
				autoFocus: false,
				data: editDialogData(row, this.permissions, payoff),
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((saved: PromiseToPayDialogResult) => {
				if (saved) this.patchRow(saved);
			});
	}

	/**
	 * Arranging also raises the KapCharge payment, server-side, in one call. The dialog
	 * collects which NLS ledger the amount posts against (a Payment — optionally a Payoff
	 * — or a Reduction) and its own context stands in for a plain confirm: the transaction
	 * is queued for the promise date the moment this returns. A loan with no active bank
	 * account first picks one of its expired or disabled accounts.
	 */
	onArrange(row: PromiseToPay): void {
		this.dialog
			.open(PromiseToPayArrangeDialogComponent, {
				width: '420px',
				autoFocus: false,
				data: { promise: row },
			})
			.afterClosed()
			.pipe(
				filter((result): result is ArrangeOptions => !!result),
				switchMap((result: ArrangeOptions) =>
					pickArrangeBankAccount(this.dialog, row).pipe(
						filter((bankAccount) => bankAccount !== undefined),
						map((bankAccount) =>
							bankAccount ? { ...result, bankAccount } : result
						)
					)
				),
				takeUntil(this.destroy$)
			)
			.subscribe((options) =>
				this.runAction(
					row,
					this.promiseToPayService.arrange(row.id, options),
					'Arranged'
				)
			);
	}

	onMarkProcessed(row: PromiseToPay): void {
		this.confirm(
			'Mark as processed',
			`Mark the ${this.typeLabel(row)} of ${FormatUtil.formatCurrency(
				row.amount
			)} due ${row.promiseDate} on loan ${row.loanNumber} as processed?`,
			() =>
				this.runAction(
					row,
					this.promiseToPayService.markProcessed(row.id),
					'Processed'
				)
		);
	}

	onMarkBroken(row: PromiseToPay): void {
		this.confirm(
			'Mark as broken',
			`Mark the ${this.typeLabel(row)} of ${FormatUtil.formatCurrency(
				row.amount
			)} due ${row.promiseDate} on loan ${row.loanNumber} as broken?`,
			() =>
				this.runAction(
					row,
					this.promiseToPayService.markBroken(row.id),
					'Broken'
				)
		);
	}

	onCancel(row: PromiseToPay): void {
		this.confirm(
			'Cancel promise',
			`Cancel the ${this.typeLabel(row)} of ${FormatUtil.formatCurrency(
				row.amount
			)} due ${row.promiseDate} on loan ${row.loanNumber}?`,
			() =>
				this.runAction(
					row,
					this.promiseToPayService.cancel(row.id),
					'Cancelled'
				)
		);
	}

	onDelete(row: PromiseToPay): void {
		this.confirm(
			'Delete promise',
			`Delete the ${this.typeLabel(row)} of ${FormatUtil.formatCurrency(
				row.amount
			)} due ${row.promiseDate} on loan ${row.loanNumber}?`,
			() =>
				this.runAction(row, this.promiseToPayService.delete(row.id), 'Deleted')
		);
	}

	private runAction(
		row: PromiseToPay,
		request$: Observable<PromiseToPay>,
		pastTense: string
	): void {
		this.busyIds.add(row.id);
		request$.pipe(takeUntil(this.destroy$)).subscribe({
			next: (updated) => {
				this.busyIds.delete(row.id);
				this.patchRow(updated);
				this.snackBar.open(`${pastTense}.`, 'Dismiss', { duration: 3000 });
			},
			error: (err) => {
				this.busyIds.delete(row.id);
				this.showError(err, `Failed to ${pastTense.toLowerCase()}.`);
			},
		});
	}

	/**
	 * Replaces the row in place rather than reloading the page — except when the row no
	 * longer matches the status or payment method filter, where it has left the page.
	 */
	private patchRow(updated: PromiseToPay): void {
		if (
			(this.filterModel.status && updated.status !== this.filterModel.status) ||
			updated.paymentMethod !== this.filterModel.paymentMethod
		) {
			this.load();
			return;
		}
		this.rows = this.rows.map((row) => (row.id === updated.id ? updated : row));
		// The schedule came from the server; re-read it so the day cards stay truthful.
		this.refreshSummary();
	}

	private refreshSummary(): void {
		if (!this.isManualDebit) return;
		this.promiseToPayService
			.list({
				type: this.filterModel.type,
				paymentMethod: this.filterModel.paymentMethod,
				loanNumber: this.filterModel.loanNumber,
				promiseDateFrom: this.toIsoDate(this.filterModel.promiseDateFrom),
				promiseDateTo: this.toIsoDate(this.filterModel.promiseDateTo),
				page: 0,
				pageSize: 1,
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (response) => {
					this.upcoming = response.upcoming;
				},
				// The day cards being briefly stale is not worth an error message.
				error: () => undefined,
			});
	}

	private confirm(title: string, content: string, onConfirm: () => void): void {
		this.dialog
			.open(LmcDialogComponent, {
				data: {
					title,
					content,
					positiveButtonText: 'Confirm',
					negativeButtonText: 'Cancel',
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((confirmed) => {
				if (confirmed) onConfirm();
			});
	}

	private showError(err: unknown, fallback: string): void {
		const body = (err as { error?: { message?: string } })?.error;
		this.snackBar.open(body?.message ?? fallback, 'Dismiss', {
			duration: 6000,
		});
	}
}
